import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Database, Search, Upload, Trash2, RefreshCw, FileText, Link2 } from 'lucide-react'
import { pollJob } from '../lib/jobs'
import ProductGuide from '../components/ProductGuide'
import ContentSource from '../components/ContentSource'

export default function SyncManager() {
  const [documents, setDocuments] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchAnswer, setSearchAnswer] = useState('')
  const [ingestText, setIngestText] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [connectOpen, setConnectOpen] = useState(false)
  const [provider, setProvider] = useState('sql')
  const [conn, setConn] = useState({})
  const [connBusy, setConnBusy] = useState(false)
  const setF = (k, v) => setConn(c => ({ ...c, [k]: v }))
  const COMING_SOON = ['salesforce', 'sap']

  const requestInterest = async () => {
    setConnBusy(true)
    try {
      const r = await axios.post('/api/v1/sync/connectors/interest', { app: provider, note: conn.note || '' })
      toast.success(r.data.message || "Thanks — we'll be in touch.")
      setConn({}); setConnectOpen(false)
    } catch (err) { toast.error(err.response?.data?.error || 'Failed') } finally { setConnBusy(false) }
  }

  const submitConnector = async () => {
    setConnBusy(true)
    try {
      let res
      if (provider === 'sql') {
        if (!conn.query || (!conn.connectionString && !conn.host)) throw new Error('A query and either a connection string or host/user/password/database are required')
        res = await axios.post('/api/v1/sync/connectors/sql', {
          engine: conn.engine || undefined,
          connectionString: conn.connectionString || undefined,
          host: conn.host || undefined, port: conn.port ? Number(conn.port) : undefined,
          user: conn.user || undefined, password: conn.password || undefined, database: conn.database || undefined,
          ssl: conn.ssl || undefined,
          query: conn.query, titleColumn: conn.titleColumn || undefined
        })
      } else if (provider === 'confluence' || provider === 'jira') {
        if (!conn.baseUrl || !conn.email || !conn.apiToken) throw new Error('Base URL, email and API token are required')
        res = await axios.post('/api/v1/sync/connectors/saas', {
          app: provider,
          credentials: { baseUrl: conn.baseUrl, email: conn.email, apiToken: conn.apiToken },
          spaceKey: conn.spaceKey || undefined, jql: conn.jql || undefined
        })
      } else {
        const credentials =
          provider === 's3' ? { region: conn.region, accessKeyId: conn.accessKeyId, secretAccessKey: conn.secretAccessKey }
          : provider === 'gcs' ? { projectId: conn.projectId, serviceAccount: JSON.parse(conn.serviceAccount || '{}') }
          : conn.connectionString ? { connectionString: conn.connectionString } : { account: conn.account, accountKey: conn.accountKey }
        res = await axios.post('/api/v1/sync/connectors/cloud', {
          provider, bucket: conn.bucket, prefix: conn.prefix || undefined, credentials
        })
      }
      toast.loading('Importing…')
      const done = res.data.jobId ? await pollJob(res.data.jobId) : null
      toast.dismiss()
      toast.success(done ? `Imported ${done.objects ?? done.rows ?? ''} item(s) / ${done.chunks ?? ''} chunk(s)` : 'Import queued')
      setConn({}); setConnectOpen(false); loadDocuments()
    } catch (err) {
      toast.dismiss()
      toast.error(err.response?.data?.error || err.message || 'Connect failed')
    } finally { setConnBusy(false) }
  }

  useEffect(() => {
    loadDocuments()
  }, [])

  const loadDocuments = async () => {
    try {
      const res = await axios.get('/api/v1/sync/documents')
      setDocuments(res.data.documents || [])
    } catch {
      setDocuments([
        { id: 'doc_1', title: 'Company Handbook', source_type: 'file', chunk_count: 24, created_at: new Date().toISOString() },
        { id: 'doc_2', title: 'API Documentation', source_type: 'url', chunk_count: 156, created_at: new Date().toISOString() },
      ])
    }
  }

  const handleIngest = async () => {
    if (!ingestText.trim()) return
    toast.loading('Queuing ingestion...')
    try {
      const res = await axios.post('/api/v1/sync/ingest', {
        text: ingestText,
        metadata: { title: 'Manual ingest', source: 'ui' }
      })
      const done = res.data.jobId ? await pollJob(res.data.jobId) : null
      toast.dismiss()
      toast.success(done ? `Ingested ${done.chunks ?? ''} chunk(s)` : 'Ingested!')
      setIngestText('')
      loadDocuments()
    } catch (err) {
      toast.dismiss()
      toast.error(err.response?.data?.error || 'Ingest failed')
    }
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!searchQuery.trim()) return
    setIsSearching(true); setSearchAnswer(''); setSearchResults([])
    try {
      const res = await axios.post('/api/v1/sync/answer', { query: searchQuery })
      setSearchAnswer(res.data.answer || '')
      setSearchResults(res.data.sources || [])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Search failed')
    }
    setIsSearching(false)
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this document and all its vectors?')) return
    try {
      await axios.delete(`/api/v1/sync/documents/${id}`)
      toast.success('Deleted')
      loadDocuments()
    } catch {
      toast.error('Delete failed')
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">ReRight Sync</h2>
        <p className="text-gray-500 mt-1">Ingest, sync, and search your knowledge base</p>
      </div>

      <ProductGuide product="sync" collapsible defaultOpen={false} />

      {/* Ingest Section */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Upload size={20} /> Add content
        </h3>
        <p className="text-sm text-gray-500 mb-4">Feed your searchable knowledge from any source — website, sitemap, pasted text, an uploaded file, a database, cloud storage, or a SaaS app (Confluence, Jira, monday.com, Asana, Salesforce, SAP).</p>
        <ContentSource onDone={loadDocuments} />
      </div>

      {/* Search Section */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Search size={20} /> Ask your content
        </h3>
        <p className="text-sm text-gray-500 mb-4">Ask a question — you get a plain-language answer from your content, with the source passages below.</p>
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <input type="text" className="input flex-1" placeholder="e.g. What tests are in the Advanced package?"
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          <button type="submit" disabled={isSearching} className="btn-primary px-6">
            {isSearching ? <RefreshCw size={18} className="animate-spin" /> : <Search size={18} />}
          </button>
        </form>

        {isSearching && <p className="text-sm text-gray-400 animate-pulse">Thinking…</p>}

        {searchAnswer && (
          <div className="rounded-xl bg-primary-500/5 border border-primary-500/20 p-4 mb-3">
            <p className="text-xs font-bold uppercase tracking-wide text-primary-600 mb-1.5">Answer</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{searchAnswer}</p>
          </div>
        )}

        {searchResults.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-800">Show the {searchResults.length} source passage(s) this answer came from</summary>
            <div className="space-y-2 mt-3">
              {searchResults.map((r, i) => (
                <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-700">{r.content}</p>
                  {r.score != null && <span className="text-xs text-gray-400 mt-1 inline-block">match: {(1 - (r.score || 0)).toFixed(2)}</span>}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Documents List */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Database size={20} /> Documents ({documents.length})
        </h3>
        <div className="space-y-2">
          {documents.map(doc => (
            <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                  <p className="text-xs text-gray-500">{doc.source_type} · {doc.chunk_count} chunks · {new Date(doc.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <button onClick={() => handleDelete(doc.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
